import { useMemo } from 'react';
import AppBar from '@material-ui/core/AppBar';
import Card from '@material-ui/core/Card';
import Divider from '@material-ui/core/Divider';
import Paper from '@material-ui/core/Paper';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { PublicKey } from '@solana/web3.js';
import { useQuery } from '@tanstack/react-query';
import strings from '../../localization';
import { fetchSupportedTokens } from '../../utils/marketplace';
import { TOKEN_PROGRAM_ID } from '../../utils/tokens/instructions';
import { useTokenAccounts } from '../../utils/tokens/hooks';
import { useIsExtensionWidth } from '../../utils/utils';
import { useMintInfos, usePoolInfo, usePools, usePosition } from '../../utils/tokenStaking/hooks';
import { PROGRAM_ID, formatBps, formatUnits } from '../../utils/tokenStaking/program';
import TokenIcon from '../TokenIcon';
import PositionCard from './PositionCard';
import StakeTokenButton from './StakeTokenButton';

const DAY = 86_400;

const useStyles = makeStyles((theme) => ({
  card: { padding: theme.spacing(2), display: 'flex', flexDirection: 'column', gap: theme.spacing(2) },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: theme.spacing(1) },
  token: { display: 'flex', alignItems: 'center', gap: theme.spacing(1) },
}));

/** Mints the wallet holds plus marketplace listings; pools are discovered on-chain from these. */
function useCandidateMints(): PublicKey[] {
  const { data: accounts } = useTokenAccounts(TOKEN_PROGRAM_ID);
  const { data: listed } = useQuery({ queryKey: ['supportedTokens'], queryFn: fetchSupportedTokens, staleTime: 5 * 60 * 1000 });
  return useMemo(() => {
    const addresses = new Set<string>();
    (accounts ?? []).forEach((account) => addresses.add(account.account.data.parsed.info.mint));
    (listed ?? []).forEach((token) => token.publicKey && addresses.add(token.publicKey));
    return Array.from(addresses).map((address) => new PublicKey(address));
  }, [accounts, listed]);
}

function PoolRow({ mint, symbol, logoUri }: { mint: PublicKey; symbol: string; logoUri: string | null }) {
  const t = strings.tokenStaking;
  const classes = useStyles();
  const { data: info } = usePoolInfo(mint);
  const { data: position } = usePosition(mint);
  if (!info) return null;
  const { pool, decimals, rewardVaultBalance } = info;
  const free = rewardVaultBalance > pool.reservedRewards ? rewardVaultBalance - pool.reservedRewards : 0n;
  const daily = (pool.totalStaked * BigInt(pool.rateBps) * BigInt(DAY)) / (10_000n * 31_536_000n);
  const runway = daily === 0n ? null : Number(free / daily);
  return (
    <div>
      <div className={classes.header}>
        <div>
          <div className={classes.token}>
            <TokenIcon mint={mint} tokenName={symbol} url={logoUri} size={24} />
            <Typography variant="subtitle1">{symbol}</Typography>
          </div>
          <Typography variant="body2" color="textSecondary">
            {formatBps(pool.rateBps)} {t.apr} · {Math.round(pool.lockSeconds / DAY)} {t.dayLock} · {t.minimum} {formatUnits(pool.minStake, decimals)} {symbol}
            {pool.paused ? ` · ${t.paused}` : ''}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {t.poolHealth}: {formatUnits(free, decimals, 2)} {symbol} {t.free}
            {runway !== null ? ` · ${runway} ${t.daysRunway}` : ''} · {formatUnits(pool.totalStaked, decimals, 2)} {symbol} {t.staked.toLowerCase()}
          </Typography>
        </div>
        {!position && <StakeTokenButton mint={mint} symbol={symbol} pool={pool} size="small" />}
      </div>
      {position && <PositionCard mint={mint} symbol={symbol} info={info} position={position} />}
    </div>
  );
}

export default function TokenStakingSection() {
  const t = strings.tokenStaking;
  const classes = useStyles();
  const isExtensionWidth = useIsExtensionWidth();
  const mints = useCandidateMints();
  const { data: pools } = usePools(mints);
  const { data: infos } = useMintInfos(mints);
  const stakable = useMemo(() => mints.filter((mint) => !!pools?.[mint.toBase58()]), [mints, pools]);

  if (!PROGRAM_ID) return null;

  return (
    <Paper>
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <Typography variant="h6" component="h2" style={{ flexGrow: 1, fontSize: isExtensionWidth ? '1rem' : undefined }}>
            {t.sectionTitle}
          </Typography>
        </Toolbar>
      </AppBar>
      <Card className={classes.card}>
        {stakable.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            {t.noPools}
          </Typography>
        ) : (
          stakable.map((mint, index) => {
            const address = mint.toBase58();
            return (
              <div key={address}>
                {index > 0 && <Divider style={{ marginBottom: 16 }} />}
                <PoolRow
                  mint={mint}
                  symbol={infos?.[address]?.symbol ?? `${address.slice(0, 4)}…`}
                  logoUri={infos?.[address]?.logoUri ?? null}
                />
              </div>
            );
          })
        )}
      </Card>
    </Paper>
  );
}
